import * as Slider from '@radix-ui/react-slider'
import { VisuallyHidden } from '@radix-ui/react-visually-hidden'
import clsx from 'clsx'
import { useCallback, useEffect, useState } from 'react'

import Nickname from '@/components/nickname'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { formatNumber } from '@/lib/formatter'
import { useCompetitionRedeemMutation } from '@/redux/competition-betting'
import { RelatedSimpleUser } from '@/redux/user'

export interface RedeemDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  competitionId: number
  competitor: RelatedSimpleUser
  maxRedeemAmount: number
  myBet: number
}

export default function RedeemDialog({
  open,
  onOpenChange,
  competitionId,
  competitor,
  maxRedeemAmount,
  myBet,
}: RedeemDialogProps) {
  const onClose = useCallback(() => onOpenChange(false), [onOpenChange])
  const [competitionRedeem, { isLoading }] = useCompetitionRedeemMutation()
  const [amount, setAmount] = useState('')
  const [ratio, setRatio] = useState(0)

  useEffect(() => {
    setAmount('')
    setRatio(0)
  }, [open])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <VisuallyHidden>
        <DialogTitle>
          Redeem on <Nickname className="inline" user={competitor} />
        </DialogTitle>
      </VisuallyHidden>
      <DialogContent className="w-120" showClose={false}>
        <div className="flex flex-col gap-6">
          <div className="text-[18px] font-semibold leading-6.5">
            Redeem on <Nickname className="inline" user={competitor} />
          </div>
          <div>
            <div className="text-sm text-secondary-foreground leading-4.5 flex justify-between items-center">
              <div>Redemption Amount</div>
              <div className="flex items-center gap-2">
                <div className="text-xs text-muted-foreground leading-4.5">
                  Max Redeem<span className="inline-flex">≈</span>
                  {formatNumber()(maxRedeemAmount)}pts
                </div>
                <div
                  className="text-xs text-primary leading-4.5 cursor-pointer"
                  onClick={() => {
                    setAmount(maxRedeemAmount.toFixed(0))
                    setRatio(100)
                  }}
                >
                  Redeem All
                </div>
              </div>
            </div>
            <div className="px-3 mt-4">
              <div className="flex">
                <Slider.Root
                  className="grow relative flex h-5 touch-none select-none items-center"
                  defaultValue={[0]}
                  max={100}
                  step={1}
                  value={[ratio]}
                  onValueChange={(value) => {
                    setRatio(value[0])
                    setAmount(((value[0] * maxRedeemAmount) / 100).toFixed(0))
                  }}
                >
                  <Slider.Track className="relative h-1 grow rounded-full bg-white/5">
                    <Slider.Range className="absolute h-full rounded-full bg-primary" />
                  </Slider.Track>
                  <Slider.Thumb
                    className="size-3 rounded-[10px] bg-primary cursor-pointer focus:outline-none flex justify-center items-center"
                    aria-label="Volume"
                  >
                    <div className="size-2 rounded-full bg-white"></div>
                  </Slider.Thumb>
                </Slider.Root>
                <div
                  className={clsx(
                    'w-12 text-sm leading-5.5 flex justify-end items-center',
                    +amount > maxRedeemAmount
                      ? 'text-(--destructive)'
                      : 'text-muted-foreground',
                  )}
                >
                  {ratio > 9999 ? 9999 : ratio}%
                </div>
              </div>
            </div>
            <div className="relative mt-3">
              <Input
                className={'pr-10'}
                aria-invalid={+amount > maxRedeemAmount}
                value={amount}
                onChange={(e) => {
                  const value = e.target.value
                  // 只允许数字输入，将非数字字符替换为空
                  const numericValue = value.replace(/[^0-9]/g, '')
                  setAmount(numericValue)
                  setRatio(
                    +((+numericValue * 100) / maxRedeemAmount).toFixed(0),
                  )
                }}
                placeholder="Enter amount to redeem (estimated)"
              ></Input>
              <div className="text-base text-muted-foreground absolute right-3 top-0 bottom-0 flex justify-end items-center">
                pts
              </div>
            </div>
            {/* <div
              className={cn(
                "mt-5 h-12 px-3 flex justify-between items-center bg-white/4 border border-white/4 rounded-xl",
                +amount > maxRedeemAmount
                  ? "border-(--color-destructive)"
                  : "border-white/4"
              )}
            >
              <input
                className="bg-transparent outline-none grow text-foreground placeholder:text-muted-foreground"
                value={amount}
                onChange={(e) => {
                  const value = e.target.value;
                  // 只允许数字输入，将非数字字符替换为空
                  const numericValue = value.replace(/[^0-9]/g, "");
                  setAmount(numericValue);
                  setRatio(
                    +((+numericValue * 100) / maxRedeemAmount).toFixed(0)
                  );
                }}
                placeholder="Enter amount to redeem (estimated)"
              />
              <div className="text-base text-muted-foreground">pts</div>
            </div> */}
            {+amount > maxRedeemAmount && (
              <div className="mt-3 text-xs text-(--color-destructive) leading-4.5 ">
                Redemption amount exceeds the limit.Please enter a valid amount.
              </div>
            )}
          </div>
          <div className="flex justify-end items-center gap-2">
            <Button
              className="px-4 h-9 rounded-full font-semibold text-sm cursor-pointer"
              variant="outline"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              className="px-4 h-9 rounded-full font-semibold text-sm cursor-pointer"
              variant="primary"
              disabled={+amount <= 0 || +amount > maxRedeemAmount}
              isLoading={isLoading}
              onClick={async () => {
                if (!competitor) return
                await competitionRedeem({
                  competitionId,
                  competitorId: competitor.id,
                  amount: +((+amount / maxRedeemAmount) * myBet).toFixed(),
                  myBet,
                  maxRedeem: maxRedeemAmount,
                  userInput: +amount,
                }).unwrap()
                onClose()
              }}
            >
              Redeem
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
